import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import find from 'lodash/find';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import moment from 'moment';
import Save2Icon from 'zignal-landscape/lib/Icon/Save2Icon';
import InformationIcon from 'zignal-landscape/lib/Icon/InformationIcon';
import BanIcon from 'zignal-landscape/lib/Icon/BanIcon';

import styles from './ImportHistory.css';
import { convertApiTimeToMoment } from 'helpers/timeFormat';

const TIMESTAMP_FORMAT = 'M/D/YYYY (h:mma)';

function getProfilesForTemplate(historyItem, profiles) {
  const profilesById = keyBy(profiles, 'id');
  // Filter out profiles that were deleted after history item was created
  const validHistoryItemProfileIds = historyItem.profiles.filter((profileId) => {
    return profilesById[profileId];
  });

  if(validHistoryItemProfileIds.length === profiles.length) {
    return 'All Datasets';
  }

  return map(validHistoryItemProfileIds, (profileId) => {
    return profilesById[profileId].name;
  })
    .join(', ');
}

function getSourcesForTemplate(historyItem, sources) {
  const filteredSources = sources.filter((s) => s.supportsHistorics !== false);
  if(historyItem.sources.length >= filteredSources.length) {
    return 'All Sources';
  }

  return map(historyItem.sources, (source) => {
    const sourceItem = find(sources, {value: source});
    if (sourceItem) {
      return sourceItem.label;
    }
    return '';
  })
    .filter(Boolean)
    .join(', ');
}

function getImportCompletedDetailsValue(historyItem, timezoneOffset) {
  const endMoment = convertApiTimeToMoment(historyItem.updated_at, timezoneOffset);
  const startMoment = convertApiTimeToMoment(historyItem.created_at, timezoneOffset);
  const difference = endMoment.diff(startMoment);
  const duration = moment.duration(difference);

  const completedText = `${endMoment.format(TIMESTAMP_FORMAT)}`;

  let durationText = 'Duration: ';
  if(duration.hours() > 0) {
    durationText += `${duration.hours()}hr `;
  }
  durationText += `${duration.minutes()}min`;

  return `${completedText} | ${durationText}`;
}

function getItemTemplateData(historyItem, profiles, sources, timezoneOffset) {
  const rangeStart = convertApiTimeToMoment(historyItem.start_time, timezoneOffset);
  const rangeEnd = convertApiTimeToMoment(historyItem.end_time, timezoneOffset);
  const templateData = {
    profiles: getProfilesForTemplate(historyItem, profiles),
    sources: getSourcesForTemplate(historyItem, sources),
    detailsLabel: 'Started',
    detailsValue: convertApiTimeToMoment(historyItem.created_at, timezoneOffset).format(TIMESTAMP_FORMAT),
    dateRange: `${rangeStart.format(TIMESTAMP_FORMAT)} to ${rangeEnd.format(TIMESTAMP_FORMAT)}`,
  };

  switch (historyItem.status) {
    case 'Succeeded':
      return Object.assign({}, templateData, {
        class: 'success',
        icon: Save2Icon,
        summary: `${historyItem.story_count.toLocaleString()} stories imported`,
        detailsLabel: 'Completed',
        detailsValue: getImportCompletedDetailsValue(historyItem, timezoneOffset),
      });
    case 'In Progress':
      return Object.assign({}, templateData, {
        class: 'inProgress',
        icon: InformationIcon,
        summary: `Importing approximately ${historyItem.story_count.toLocaleString()} stories`,
      });
    case 'Queued':
      return Object.assign({}, templateData, {
        class: 'queued',
        icon: InformationIcon,
        summary: `Queued import for approximately ${historyItem.story_count.toLocaleString()} stories`,
      });
    case 'Failed':
      return Object.assign({}, templateData, {
        class: 'failure',
        icon: BanIcon,
        summary: 'Import Unsuccessful',
      });
    case 'Cancelled':
      return Object.assign({}, templateData, {
        class: 'cancelled',
        icon: BanIcon,
        summary: 'Import Unsuccessful',
      });
    default:
      return {};
  }
}

const ImportHistory = ({
  historyItems = [],
  sources = [],
  profiles = [],
  timezoneOffset = 0
}) => {
  const placeholder = (
    <span className={styles.placeholder}>
      <p>You have not imported any historic data.</p>
    </span>
  );

  const listItems = historyItems.map((historyItem) => {
    const data = getItemTemplateData(historyItem, profiles, sources, timezoneOffset);
    const Icon = data.icon;

    return (
      <li key={historyItem.id} className={classnames(styles.ImportHistoryItem, styles[data.class])}>
        <div className={styles.iconContainer}>
          <Icon width={24} height={24} />
        </div>
        <div className={styles.textContainer}>
          <span data-cy="historic-summary" className={styles.ItemSummary} title={data.summary}>{data.summary}</span>
          <table>
            <tbody>
              <tr>
                <td className={styles.ItemLabel}>{data.detailsLabel}:</td>
                <td className={styles.ItemValue}>{data.detailsValue}</td>
              </tr>
              <tr>
                <td className={styles.ItemLabel}>Profiles:</td>
                <td className={styles.ItemValue}>{data.profiles}</td>
              </tr>
              <tr>
                <td className={styles.ItemLabel}>Sources:</td>
                <td className={styles.ItemValue}>{data.sources}</td>
              </tr>
              <tr>
                <td className={styles.ItemLabel}>Date Range:</td>
                <td className={styles.ItemValue}>{data.dateRange}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </li>
    );
  });

  return (
    <div className={styles.ImportHistory}>
      <b>Import History</b>
      <ul>
        {listItems.length === 0 ? placeholder : listItems}
      </ul>
    </div>
  );
};

ImportHistory.propTypes = {
  historyItems: PropTypes.array,
  sources: PropTypes.array,
  profiles: PropTypes.array,
  timezoneOffset: PropTypes.number
};

export default ImportHistory;
